import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import DevicesService from "common/services/devices/DevicesService";
import UsersService from "common/services/users/UsersService";

/** Async Thunks */

/** ADMIN ACTIONS */
export const listGateways = createAsyncThunk("gateways/listAll", async () => {
  try {
    const response = await DevicesService.listGateways();
    return response.data;
  } catch (error) {
    if (error.response.status === 404) {
      return [];
    }
    console.log("Error fetching gateways list: ", error);
    throw error;
  }
});

export const associateUserToGateway = createAsyncThunk("gateways/associateUser", async ({username, email, gatewayId, provisioningCode, licenceId}, {rejectWithValue}) => {
  try {
    const response = await DevicesService.associateUserToGateway(gatewayId, provisioningCode, licenceId, username, email);
    console.log(response.data)
    const success = response?.data?.success || [];
    const failed = response?.data?.failed || [];
    const idxSuccess = success.findIndex(e => e.gatewayId === gatewayId);
    if (idxSuccess !== -1) return success[idxSuccess];
    const idxFailed = failed.findIndex(e => e.gatewayId === gatewayId);
    if (idxFailed !== -1) {
      return rejectWithValue(failed[idxFailed].error);
    } else {
      return rejectWithValue("InternalError");
    }
  } catch (error) {
    console.error(error);
    return rejectWithValue("InternalError");
  }
});

export const removeUserGWAssociation = createAsyncThunk("gateways/removeUser", async ({username, gatewayId}) => {
  try {
    const response = await DevicesService.removeUserGWAssociation(username, gatewayId);
    return response.data;
  } catch (error) {
    console.log("Error disassociating user from gateway: ", error);
    throw error;
  }
});

export const addGateway = createAsyncThunk("gateways/add", async ({id, provisioningCode}) => {
  try {
    const response = await DevicesService.addGateway(id, provisioningCode);
    console.log(response.data);
    return response.data;
  } catch (error) {
    console.log("Error adding new gateway: ", error);
    throw error;
  }
});

export const deleteGateway = createAsyncThunk("gateways/deleteOne", async(id, { rejectWithValue }) => {
  try {
    const response = await DevicesService.deleteGateway(id);
    return {...response.data, gatewayId: id};
  } catch (error) {
    console.log("Error deleting gateway: ", error);
    return rejectWithValue(error);
  }
});

export const getGatewayUsers = createAsyncThunk("gateways/getUsers", async(gatewayId, { rejectWithValue }) => {
  try {
    const response = await DevicesService.getGateway(gatewayId);
    const gateway = response.data;
    return gateway.users;
  } catch (error) {
    console.log("Error deleting gateway: ", error);
    return rejectWithValue(error);
  }
});

/** USER ACTIONS */

export const listUserGateways = createAsyncThunk("gateways/listByUser", async (user) => {
  try {
    const response = await DevicesService.listUserGateways(user);
    return response.data;
  } catch (error) {
    console.log("Error fetching gateways list: ", error);
    throw error;
  }
});

/** OTHER ACTIONS */

export const changeName = createAsyncThunk("gateways/setName", async ({user, id, name, userLevel, type}, { rejectWithValue }) => {
  try {
    if (type !== "gateway" && type !== "device") {
      console.error("TYPE NOT VALID");
      return;
    }

    const body = { [`${type}Names`]: { [id]: name } };
    await UsersService.editSettings(user, body);
    return {id, name, userLevel, type};
  } catch (error) {
    return rejectWithValue(error);
  }
});

/** Initial State */

const initialState = {
  items: [],
  userItems: [],
  loading: {
    devices: true,
    disassociate: false,
    associatedUsers: false
  },
  associatedUsers: []
};

/** Slice */

export const gatewaysSlice = createSlice({
  name: "gateways",
  initialState,
  reducers: {
    deviceStatusUpdated: (state, action) => {
      let items; 
      // State is updated in the admin devices list
      if (action.payload.level === 0) {
        items = state.items
      } 
      // State is updated in the user devices list
      else if ([1 ,2].includes(action.payload.level)) {
        items = state.userItems;
      } else {
        // User level invalid
        return;
      }
      const idx = items.findIndex(e => e.id === action.payload.id);
      // Item not found
      if (idx === -1) return;
      
      let itemUpdated = items[idx];

      const updatedFields = action.payload.body;

      for (const field in updatedFields) {
        // If Gateway is disconnected, devices status is unknown
        if (field === "connectionStatus" && updatedFields[field] === "offline") {
          itemUpdated.devices.map(e => e.status = "");
        }
        if (field !== "devices") {
          itemUpdated[field] = updatedFields[field];
        } else {
          // Remove "old" devices
          const newDevices = [];
          for (const device of itemUpdated.devices) {
            if (device.id in updatedFields[field]) {
              newDevices.push(device);
            }
          }
          // Add "new" devices
          for (const devId in updatedFields[field]) {
            const devIndex = itemUpdated.devices.findIndex(e => e.id === devId);
            if (devIndex === -1) {
              updatedFields[field][devId].id = devId;
              newDevices.push(updatedFields[field][devId]);
            }
          }
          // Update devices
          newDevices.map(e => {
            e.status = updatedFields[field][e.id].status;
            e.anomaly = updatedFields[field][e.id].anomaly
          });
          itemUpdated.devices = newDevices;
        }
        // Update lastUpdate with ts name
        itemUpdated.lastUpdate = action.payload.ts;
      }
      items[idx] = itemUpdated;
    },
    resetAssociatedUsers: (state) => {
      state.associatedUsers = [];
    }
  },    
  extraReducers: builder => {
    builder.addCase(listGateways.pending, (state, action) => {
      state.loading.devices = true;
    });
    builder.addCase(listGateways.fulfilled, (state, action) => {
      if (!action.payload.gateways) {     // Case where there is not even 1 gateway
        state.items = [];
      } else {
        state.items = action.payload.gateways;
      }
      state.loading.devices = false;
    });
    builder.addCase(listGateways.rejected, (state, action) => {
      state.loading.devices = false;
    });
    builder.addCase(removeUserGWAssociation.pending, (state, action) => {
      state.loading.disassociate = true;
    });
    builder.addCase(removeUserGWAssociation.fulfilled, (state, action) => {
      state.loading.disassociate = false;
    });
    builder.addCase(addGateway.fulfilled, (state, action) => {
      state.items.push(action.payload)
    });
    builder.addCase(deleteGateway.fulfilled, (state, action) => {
      let items = state.items;
      const idx = items.findIndex(e => e.id === action.payload.gatewayId)
      if (idx !== -1) {
        items.splice(idx, 1);
        state.items = items;
      }
    });
    builder.addCase(listUserGateways.pending, (state, action) => {
      state.loading.devices = true;
    });
    builder.addCase(listUserGateways.fulfilled, (state, action) => {
      state.userItems = action.payload.gateways;
      state.loading.devices = false;
    });
    builder.addCase(changeName.fulfilled, (state, action) => {
      let items; 
      // State is updated in the admin devices list
      if (action.payload.userLevel === 0) {
        items = state.items
      } 
      // State is updated in the user devices list
      else if ([1 ,2].includes(action.payload.userLevel)) {
        items = state.userItems;
      } else {
        // User level invalid
        return;
      }
      console.log("ITEMS: ", items)
      if (action.payload.type === "gateway") {
        const idx = items.findIndex(e => e.id === action.payload.id);
        if (idx !== -1) {
          items[idx].name = action.payload.name
        }
      } else if (action.payload.type === "device") {
        for (let i=0; i < items.length; i++) {
          const deviceIdx = items[i].devices.findIndex(e => e.id === action.payload.id);
          if (deviceIdx !== -1) {
            items[i].devices[deviceIdx].name = action.payload.name
          }
        }
      } else {
        console.error("TYPE NOT VALID");
        return;
      }
    });
    builder.addCase(getGatewayUsers.pending, (state, action) => {
      state.loading.associatedUsers = true;
    });
    builder.addCase(getGatewayUsers.fulfilled, (state, action) => {
      state.associatedUsers = action.payload;
      state.loading.associatedUsers = false;
    });
  },
});

export const { deviceStatusUpdated, resetAssociatedUsers } = gatewaysSlice.actions;
export default gatewaysSlice.reducer;
